import React, { useState, useEffect } from 'react';
import { 
  sendEmail, 
  checkEmailConfig, 
  updateEmailConfig 
} from '../utils/nodemailerService';
import { toast } from 'react-toastify';

const EmailSender = () => {
  const [recipient, setRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [emailConfig, setEmailConfig] = useState({
    emailUser: '',
    emailPassword: ''
  });

  // Check if email is configured on component mount
  useEffect(() => {
    const verifyEmailConfig = async () => {
      try {
        const config = await checkEmailConfig();
        setIsConfigured(config.isConfigured);
        if (!config.isConfigured) {
          setShowConfig(true);
        }
      } catch (error) {
        console.error('Error checking email config:', error);
        setIsConfigured(false);
        setShowConfig(true);
      }
    };

    verifyEmailConfig();
  }, []);

  const handleSendEmail = async (e) => {
    e.preventDefault();
    
    if (!recipient || !subject || !message) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await sendEmail({
        to: recipient,
        subject,
        message: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 5px;">
            <h2 style="color: #333; text-align: center;">NSTP Program Notification</h2>
            <h3>${subject}</h3>
            <div>${message}</div>
            <p style="margin-top: 20px;">Best regards,</p>
            <p><strong>NSTP Administration Team</strong></p>
          </div>
        `
      });
      
      if (result.success) {
        toast.success('Email sent successfully');
        // Clear the form
        setRecipient('');
        setSubject('');
        setMessage('');
      } else {
        toast.error(`Failed to send email: ${result.message}`);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(`Error sending email: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = async (e) => {
    e.preventDefault();
    
    if (!emailConfig.emailUser || !emailConfig.emailPassword) {
      toast.error('Please fill in all configuration fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await updateEmailConfig(emailConfig);
      
      if (result.success) {
        toast.success('Email configuration updated successfully');
        setIsConfigured(true);
        setShowConfig(false);
      } else {
        toast.error(`Failed to update configuration: ${result.message}`);
      }
    } catch (error) {
      console.error('Error updating email config:', error);
      toast.error(`Error updating email configuration: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card shadow-sm p-4 mb-4">
      <h4 className="mb-4">Send Email</h4>

      {!isConfigured && (
        <div className="alert alert-warning">
          <p className="mb-0">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Email service is not configured. 
            {!showConfig && (
              <button 
                className="btn btn-link p-0 ms-2" 
                onClick={() => setShowConfig(true)}
              >
                Configure now
              </button>
            )}
          </p>
        </div>
      )}

      {showConfig && (
        <div className="card mb-4 border-info">
          <div className="card-header bg-info text-white">
            <h5 className="mb-0">Configure Email Settings</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleUpdateConfig}>
              <div className="mb-3">
                <label htmlFor="emailUser" className="form-label">Email Address</label>
                <input
                  type="email"
                  id="emailUser"
                  className="form-control"
                  value={emailConfig.emailUser}
                  onChange={(e) => setEmailConfig({...emailConfig, emailUser: e.target.value})}
                  required
                />
                <div className="form-text">Use a Gmail address</div>
              </div>
              
              <div className="mb-3">
                <label htmlFor="emailPassword" className="form-label">App Password</label>
                <input
                  type="password"
                  id="emailPassword"
                  className="form-control"
                  value={emailConfig.emailPassword}
                  onChange={(e) => setEmailConfig({...emailConfig, emailPassword: e.target.value})}
                  required
                />
                <div className="form-text">
                  For Gmail, use an <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer">App Password</a>
                </div>
              </div>
              
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : 'Save Configuration'}
              </button>
              
              {isConfigured && (
                <button 
                  type="button" 
                  className="btn btn-outline-secondary ms-2" 
                  onClick={() => setShowConfig(false)}
                >
                  Cancel
                </button>
              )}
            </form>
          </div>
        </div>
      )}

      {(isConfigured || !showConfig) && (
        <form onSubmit={handleSendEmail}>
          <div className="mb-3">
            <label htmlFor="recipient" className="form-label">Recipient Email</label>
            <input
              type="email"
              id="recipient"
              className="form-control"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              required
              disabled={!isConfigured || loading}
            />
          </div>
          
          <div className="mb-3">
            <label htmlFor="subject" className="form-label">Subject</label>
            <input
              type="text"
              id="subject"
              className="form-control"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              disabled={!isConfigured || loading}
            />
          </div>
          
          <div className="mb-3">
            <label htmlFor="message" className="form-label">Message</label>
            <textarea
              id="message"
              className="form-control"
              rows="5"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              disabled={!isConfigured || loading}
            ></textarea>
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={!isConfigured || loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Sending...
              </>
            ) : 'Send Email'}
          </button>
          
          {isConfigured && (
            <button 
              type="button" 
              className="btn btn-link text-secondary" 
              onClick={() => setShowConfig(!showConfig)}
            >
              {showConfig ? 'Hide Configuration' : 'Show Configuration'}
            </button>
          )}
        </form>
      )}
    </div>
  );
};

export default EmailSender; 